import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import jwtDecode from "jwt-decode";
import axios from "../api/client";
import { setupRefreshInterceptor } from "../utils/setupRefreshInterceptor";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [statusColorConfig, setStatusColorConfig] = useState(null);
  const [invoicingEnabled, setInvoicingEnabled] = useState(true);
  const [tenantModule, setTenantModule] = useState("dms");
  const [enabledModules, setEnabledModules] = useState(["po", "inventory"]);

  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const fetchTenantStatusColors = useCallback((currentTenantModule) => {
    axios
      .get("/tenant")
      .then((res) => {
        setStatusColorConfig(res?.data?.statusColorConfig || null);
        const schema = res?.data?.projectFormSchema || {};
        setInvoicingEnabled(schema.invoicingEnabled !== false);
        if (res?.data?.module) {
          setTenantModule(res.data.module);
        }
      })
      .catch(() => {
        // non-fatal (e.g. expired subscription blocks /tenant): use defaults
      });

    // Only relevant (and only routed) for manufacturing tenants — calling it for
    // DMS tenants 403s with MODULE_MISMATCH, which the global axios interceptor
    // treats as a hard redirect trigger, so it must be skipped for them.
    if (currentTenantModule === "manufacturing") {
      axios
        .get("/manufacturing/module-status")
        .then((res) => {
          if (Array.isArray(res?.data?.enabledModules)) {
            setEnabledModules(res.data.enabledModules);
          }
        })
        .catch(() => {
          // non-fatal: fall back to cached enabledModules
        });
    }
  }, []);

  const login = async (email, password) => {
    const resp = await axios.post("/auth/login", { email, password });
    const { accessToken, refreshToken } = resp.data;

    const decoded = jwtDecode(accessToken);

    setAccessToken(accessToken);
    localStorage.setItem("accessToken", accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }

    setTenantModule(decoded.tenantModule || "dms");
    setUser({
      id: decoded.userId,
      firstName: decoded.firstName,
      lastName: decoded.lastName,
      email: decoded.email,
      role: decoded.role,
      roles: decoded.roles || (decoded.role ? [decoded.role] : []),
      tenantId: decoded.tenantId,
      tenantName: decoded.tenantName,
      mustChangePassword: decoded.mustChangePassword,
      isAdmin: decoded.isAdmin,
      permissions: decoded.permissions || [],
    });

    fetchTenantStatusColors(decoded.tenantModule || "dms");
  };

  const logout = async (skipApi = false) => {
    if (!skipApi) {
      try {
        await axios.post("/auth/logout");
      } catch (e) {
        // ignore
      }
    }
    setUser(null);
    setAccessToken(null);
    setStatusColorConfig(null);
    setInvoicingEnabled(true);
    setTenantModule("dms");
    setEnabledModules(["po", "inventory"]);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    delete axios.defaults.headers.common.Authorization;
  };

  useEffect(() => {
    setupRefreshInterceptor({ setAccessToken, logout });
  }, []);

  const updateUser = (fields) => {
    setUser((prev) => ({ ...prev, ...fields }));
  };

  const refresh = useCallback(async () => {
    try {
      const storedRefreshToken = localStorage.getItem("refreshToken");
      if (!storedRefreshToken) {
        setIsLoading(false);
        return;
      }
      const resp = await axios.post(
        "/auth/refresh",
        storedRefreshToken ? { refreshToken: storedRefreshToken } : undefined,
        storedRefreshToken
          ? { headers: { "x-refresh-token": storedRefreshToken } }
          : undefined,
      );
      const { accessToken, refreshToken } = resp.data;
      const decoded = jwtDecode(accessToken);
      setAccessToken(accessToken);
      localStorage.setItem("accessToken", accessToken);
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      setTenantModule(decoded.tenantModule || "dms");
      setUser({
        id: decoded.userId,
        firstName: decoded.firstName,
        lastName: decoded.lastName,
        email: decoded.email,
        role: decoded.role,
        roles: decoded.roles || (decoded.role ? [decoded.role] : []),
        tenantId: decoded.tenantId,
        tenantName: decoded.tenantName,
        mustChangePassword: decoded.mustChangePassword,
        isAdmin: decoded.isAdmin,
        permissions: decoded.permissions || [],
      });

      fetchTenantStatusColors(decoded.tenantModule || "dms");
    } catch (err) {
      setUser(null);
      setAccessToken(null);
      setStatusColorConfig(null);
      setInvoicingEnabled(true);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
    } finally {
      setIsLoading(false);
    }
  }, [fetchTenantStatusColors]);

  const refreshCalled = useRef(false);
  useEffect(() => {
    if (refreshCalled.current) return;
    refreshCalled.current = true;
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        setAccessToken,
        isLoading,
        isAuthenticated,
        login,
        logout,
        updateUser,
        statusColorConfig,
        setStatusColorConfig,
        invoicingEnabled,
        setInvoicingEnabled,
        tenantModule,
        setTenantModule,
        enabledModules,
        setEnabledModules,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
